"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.LotTrackingFefoClient = void 0;
const _ = require("lodash");
const base_client_1 = require("@deliverr/base-client");
const shared_1 = require("../../../shared");
const client_auth_1 = require("@deliverr/client-auth");
const core_auth_1 = require("@deliverr/core-auth");
const commons_logs_1 = require("@deliverr/commons-logs");
const LOT_TRACKING_FEFO_BASE_URL = process.env.LOT_TRACKING_FEFO_BASE_URL;
class LotTrackingFefoClient extends base_client_1.ApiClient {
    constructor(config) {
        const baseURL = `${config.baseURL || LOT_TRACKING_FEFO_BASE_URL}`;
        if (!config.baseURL) {
            const errorMessage = "LOT_TRACKING_FEFO_BASE_URL is not set/provided";
            commons_logs_1.log.warn(errorMessage);
            throw new Error(errorMessage);
        }
        if (config.authEngine === core_auth_1.AuthEngine.COGNITO_INTERNAL) {
            const tokenHandler = new client_auth_1.OAuthClientTokenBuilder(config);
            super({
                authEngine: core_auth_1.AuthEngine.COGNITO_INTERNAL,
                tokenRefreshFn: tokenHandler.getTokenFn(),
                baseURL,
            });
        }
        else {
            super({
                authEngine: core_auth_1.AuthEngine.COGNITO,
                baseURL,
            });
        }
    }
    async getLotState(input) {
        try {
            return await this.get(`/lotState/${input.dsku}`, {
                lotNumber: input.lotNumber,
                expirationDate: input.expirationDate,
            });
        }
        catch (error) {
            return this.handleError(error, { error, input });
        }
    }
    async upsertLotState(input) {
        try {
            return await this.put(`/lotState/state`, input);
        }
        catch (error) {
            return this.handleError(error, { error, input });
        }
    }
    async instantQuarantine(input) {
        try {
            return await this.put(`/${input.dsku}/instantQuarantine`, {
                sellerId: input.sellerId,
                lotNumber: input.lotNumber,
                expirationDate: input.expirationDate,
                sourceUser: input.sourceUser,
                state: input.state,
            });
        }
        catch (error) {
            return this.handleError(error, { error, input });
        }
    }
    async instantUnquarantine(input) {
        try {
            return await this.put(`/${input.dsku}/instantUnquarantine`, {
                sellerId: input.sellerId,
                lotNumber: input.lotNumber,
                expirationDate: input.expirationDate,
                sourceUser: input.sourceUser,
            });
        }
        catch (error) {
            return this.handleError(error, { error, input });
        }
    }
    async remove(input) {
        try {
            return await this.put(`/${input.dsku}/remove`, {
                sellerId: input.sellerId,
                lotNumber: input.lotNumber,
                expirationDate: input.expirationDate,
                address: input.address,
                sourceUser: input.sourceUser,
            });
        }
        catch (error) {
            return this.handleError(error, { error, input });
        }
    }
    async dispose(input) {
        try {
            return await this.put(`/${input.dsku}/dispose`, {
                sellerId: input.sellerId,
                lotNumber: input.lotNumber,
                expirationDate: input.expirationDate,
                sourceUser: input.sourceUser,
            });
        }
        catch (error) {
            return this.handleError(error, { error, input });
        }
    }
    async getFefoLots(dsku, options) {
        try {
            return await this.get(`${dsku}/fefoLots`, options);
        }
        catch (error) {
            return this.handleError(error, { error, dsku, options });
        }
    }
    async getPrepLabel(request) {
        try {
            return await this.get(`/prepLabel`, request);
        }
        catch (error) {
            return this.handleError(error, { error, request });
        }
    }
    async setGlobalStopShipByDays(sellerId, request) {
        try {
            return await this.put(`/globalStopShipByDays/${sellerId}`, request);
        }
        catch (error) {
            return this.handleError(error, { error, request });
        }
    }
    async getGlobalStopShipByDays(sellerId) {
        try {
            return await this.get(`/globalStopShipByDays/${sellerId}`);
        }
        catch (error) {
            return this.handleError(error, { error, sellerId });
        }
    }
    async setStopShipByDaysConfiguration(sellerId, request) {
        try {
            return await this.put(`/stopShipByDays/configuration/${sellerId}`, request);
        }
        catch (error) {
            return this.handleError(error, { error, request });
        }
    }
    async getStopShipByDays(sellerId) {
        try {
            return await this.get(`/stopShipByDays/${sellerId}`);
        }
        catch (error) {
            return this.handleError(error, { error, sellerId });
        }
    }
    async setSkuStopShipByDaysBulk(sellerId, formData) {
        try {
            return await this.postWithHeaders(`/skuStopShipByDays/bulk/${sellerId}`, formData, {}, {
                "Content-Type": "multipart/form-data",
            });
        }
        catch (error) {
            return this.handleError(error, { error, sellerId });
        }
    }
    /**
     * Helper function to handle errors and map them to the OptionalApiResponse type
     * This is an adapted version based on the `handleApiError` in lib-commons
     * @param error
     * @param logCtx log context containing any data that should be logged. The error will always be included in the log.
     * @private
     */
    handleError(error, logCtx = {}) {
        logCtx = {
            ...logCtx,
            error,
        };
        // Handle unknown/unexpected error type thrown
        if (!_.get(error, "isAxiosError", false)) {
            commons_logs_1.log.error(logCtx, "An unexpected error type was thrown");
            return {
                error: {
                    code: shared_1.LotTrackingErrorCodes.UNKNOWN_ERROR,
                    message: "An unknown error occurred",
                },
            };
        }
        const axiosError = error;
        // Handle client errors (i.e. ECONNREFUSED, ECONNRESET or ECONABORTED)
        if (!axiosError.response?.status) {
            commons_logs_1.log.error(logCtx, "Client error trying to connect to server");
            return {
                error: {
                    code: shared_1.LotTrackingErrorCodes.UNKNOWN_ERROR,
                    message: "Client error trying to connect to server",
                },
            };
        }
        // Handle basic verification of error payload shape
        const responseData = _.get(axiosError, "response.data");
        if (_.has(responseData, "error.code") && _.has(responseData, "error.message")) {
            return responseData;
        }
        // Handle unexpected error payload shape
        commons_logs_1.log.error(logCtx, "An unknown error was returned");
        return {
            error: {
                code: shared_1.LotTrackingErrorCodes.UNKNOWN_ERROR,
                message: "Unknown error was returned",
            },
        };
    }
}
exports.LotTrackingFefoClient = LotTrackingFefoClient;
//# sourceMappingURL=LotTrackingFefoClient.js.map